import { useEffect, useState } from "react";
import { api, money, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Wallet, PiggyBank, Landmark } from "lucide-react";

export default function AdminPayouts() {
  const [data, setData] = useState(null);
  const [batches, setBatches] = useState([]);
  const load = () => {
    api.get("/admin/payouts").then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/admin/payout-batches").then((r) => setBatches(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const runPayouts = async () => {
    try {
      const { data: res } = await api.post("/admin/payouts/run");
      toast.success(res.batches_created ? `${res.batches_created} statement(s) created — $${res.total.toFixed(2)} total` : "Nothing due right now");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markPaid = async (b) => {
    try { await api.post(`/admin/payout-batches/${b.id}/mark-paid`); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const settle = async (row) => {
    try {
      const { data: res } = await api.post(`/admin/payouts/${row.coach_id}/settle`);
      toast.success(`Marked ${res.settled_bookings} booking(s) paid out to ${row.coach_name}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-20 text-center text-zinc-500">Loading payouts…</div>;

  const cards = [
    { icon: PiggyBank, label: "Platform earnings", value: money(data.platform_earnings) },
    { icon: Wallet, label: "Owed to coaches", value: money(data.total_owed) },
    { icon: Landmark, label: "Already paid out", value: money(data.total_settled) },
  ];

  return (
    <div data-testid="admin-payouts-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <span className="text-[11px] uppercase tracking-[0.3em] text-[#ff3b30]">Admin</span>
      <h1 className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold mt-3">Payout ledger</h1>
      <p className="text-sm text-zinc-400 mt-4 max-w-xl leading-relaxed">Payments land in your Stripe account. Statements are generated automatically every Monday from completed sessions — send the money, then mark the statement paid.</p>

      <button data-testid="run-payouts-btn" onClick={runPayouts}
        className="mt-6 bg-[#00e5ff] text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#00b3cc] transition-colors duration-200">
        Run payouts now
      </button>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map(({ icon: Icon, label, value }) => (
          <div key={label} data-testid={`payout-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border border-white/10 bg-[#121212] p-6">
            <Icon className="w-4 h-4 text-[#00e5ff]" />
            <p className="display text-3xl font-bold mt-3 leading-none">{value}</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 border border-white/10">
        {data.rows.length === 0 && <p data-testid="no-payouts" className="p-10 text-sm text-zinc-500">No paid bookings yet.</p>}
        {data.rows.map((r) => (
          <div key={r.coach_id} data-testid={`payout-row-${r.coach_id}`} className="p-5 bg-[#121212] border-b border-white/10 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-4">
              <p className="display uppercase font-bold text-lg leading-none">{r.coach_name}</p>
              <p className="text-[11px] text-zinc-500 mt-1">{r.category} · {r.bookings} paid booking(s)</p>
            </div>
            <div className="md:col-span-2 text-xs text-zinc-400">{money(r.gross)} gross</div>
            <div className="md:col-span-2 text-xs text-zinc-400">{money(r.platform_fee)} fees kept</div>
            <div className="md:col-span-2 text-sm font-semibold text-[#00e5ff]">
              {money(r.owed)} owed
              <span className="block text-[10px] text-zinc-500 font-normal">{money(r.settled)} settled</span>
            </div>
            <div className="md:col-span-2 md:text-right">
              <button
                data-testid={`settle-btn-${r.coach_id}`}
                disabled={r.owed <= 0}
                onClick={() => settle(r)}
                className="text-[10px] uppercase tracking-widest border border-[#00e5ff] text-[#00e5ff] px-3 py-2 hover:bg-[#00e5ff] hover:text-black disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#00e5ff] transition-colors duration-200"
              >
                Mark paid out
              </button>
            </div>
          </div>
        ))}
      </div>
      <h2 className="display text-2xl uppercase tracking-tight font-bold mt-14">Payout statements</h2>
      <div className="mt-4 border border-white/10">
        {batches.length === 0 && <p data-testid="no-batches" className="p-10 text-sm text-zinc-500">No statements generated yet.</p>}
        {batches.map((b) => (
          <div key={b.id} data-testid={`batch-row-${b.id}`} className="p-5 bg-[#121212] border-b border-white/10 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-4">
              <p className="display uppercase font-bold text-lg leading-none">{b.coach_name}</p>
              <p className="text-[11px] text-zinc-500 mt-1">{b.bookings} session(s) · {new Date(b.created_at).toLocaleDateString()}</p>
            </div>
            <div className="md:col-span-3 text-sm font-semibold text-[#00e5ff]">{money(b.amount)}</div>
            <div className="md:col-span-3 text-xs text-zinc-400">{b.payout_method || "no payout method on file"}{b.payout_details ? ` · ${b.payout_details}` : ""}</div>
            <div className="md:col-span-2 md:text-right">
              {b.status === "paid" ? (
                <span className="text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-500/40 px-3 py-1.5">paid</span>
              ) : (
                <button data-testid={`mark-paid-${b.id}`} onClick={() => markPaid(b)}
                  className="text-[10px] uppercase tracking-widest border border-[#00e5ff] text-[#00e5ff] px-3 py-2 hover:bg-[#00e5ff] hover:text-black transition-colors duration-200">
                  Mark paid
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
